import React, { Suspense, useCallback, useEffect, useState } from "react";
import { createBrowserHistory } from "history";
import { BrowserRouter } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";
import { configure } from "mobx";
import { Routes } from "src/screens/Routes";
import { RootStore, RootStoreContext } from "src/stores/RootStore";
import "@wwimmo/ui/dist/index.css";
import { UI, initUI } from "@wwimmo/ui";
import "src/App.css";
import { Loading } from "src/screens";
import { Error } from "src/screens";
import { Notifier } from "./components/notifications/Notifier";
import { ToastContainer as NotificationContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { NetworkConfig } from "./network/NetworkConfig";
import { Config } from "./config/config";
import { GraphqlClient } from "./network/GraphqlClient";
import { initializeApolloClient } from "src/network/apolloClientInstance";
import { Route } from "src/config/routes";

configure({ enforceActions: "always" });

const history = createBrowserHistory();
const rootStore = new RootStore();
rootStore.navStore.setTitle("");

initUI({
    logo: NetworkConfig.brandAssets.logoImage,
    appname: Config.appname
});

const sentryInitObject = {
    sampleRate: 0.1,
    tracesSampleRate: 0.1,
    environment: process.env.REACT_APP_ENVIRONMENT,
    debug: false,
    dsn: NetworkConfig.sentryUrl,
    integrations: [
        new Integrations.BrowserTracing({
            routingInstrumentation: Sentry.reactRouterV5Instrumentation(history)
        })
    ]
};

const App: React.FC = () => {
    const [isInitializing, setIsInitializing] = useState(true);

    useEffect(() => {
        const initialize = async () => {
            try {
                const response = await fetch(`${window.location.origin}/ec/config.json`);
                if (response.ok) {
                    const config = await response.json();

                    NetworkConfig.graphqlWssUrl = `wss://ww-${config.env}-hasura-cidaas-wa.azurewebsites.net/v1/graphql`;
                    NetworkConfig.graphqlUrl = `https://ww-${config.env}-hasura-cidaas-wa.azurewebsites.net/v1/graphql`;

                    NetworkConfig.cidaasBaseUrl = config.cidaasBaseUrl;
                    NetworkConfig.wwEasyContactClientId = config.wwEasyContactClientId;
                    NetworkConfig.wwEasyContactClientSecret = config.wwEasyContactClientSecret;
                    NetworkConfig.wwEasyContactClientUsername = config.wwEasyContactClientUsername;
                    NetworkConfig.wwEasyContactClientPassword = config.wwEasyContactClientPassword;

                    NetworkConfig.wwEasyContactPasswordClientId = config.wwEasyContactPasswordClientId;

                    NetworkConfig.environment = config.env;

                    initializeApolloClient(rootStore);

                    if (config.Sentry.Active) {
                        const sampleRate = config.Sentry.SampleRate;
                        Sentry.init({
                            ...sentryInitObject,
                            sampleRate: sampleRate ?? 0.1,
                            tracesSampleRate: sampleRate ?? 0.1
                        });
                    }

                    if (config.maintenanceNoRedirectSubdomains && config.maintenanceNoRedirectSubdomains.length > 0) {
                        NetworkConfig.isMaintenanceActive = true;

                        const host = window.location.host.split(".")[0];
                        if (!config.maintenanceNoRedirectSubdomains.includes(host)) {
                            window.location.href = `${window.location.origin}${Route.portalUserInfoPage}`;
                        }
                    }
                }
            } catch (error) {
                Sentry.init(sentryInitObject);
                console.error("Error fetching config:", error);
            } finally {
                setIsInitializing(false);
            }
        };

        initialize();
    }, []);

    const fallback = useCallback(
        ({ error, componentStack }: { error: Error; componentStack: string | null }) => (
            <Error error={error} componentStack={componentStack} />
        ),
        []
    );

    if (isInitializing) {
        return (
            <UI.Container className="d-flex flex-fill justify-content-center align-items-center fullscreen">
                <UI.Row>
                    <UI.Col className="text-center">
                        <UI.RotatingSpinner noLogo />
                    </UI.Col>
                </UI.Row>
            </UI.Container>
        );
    }

    return (
        <Suspense fallback={<Loading fullscreen />}>
            <RootStoreContext.Provider value={rootStore}>
                <Sentry.ErrorBoundary fallback={fallback}>
                    <GraphqlClient>
                        <Notifier />
                        <NotificationContainer position="bottom-left" newestOnTop={true} />
                        <BrowserRouter basename="/ec">
                            <Routes />
                        </BrowserRouter>
                    </GraphqlClient>
                </Sentry.ErrorBoundary>
            </RootStoreContext.Provider>
        </Suspense>
    );
};

export default Sentry.withProfiler(App);
